import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import { useRouter } from "next/router";
import StoreContext from "./store";

const UserContext = createContext();

export function UserProvider({ children }) {
  const [hydrated, setHydrated] = useState(false);
  const [userData, setUserData] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);
  const [errorUser, setErrorUser] = useState(null);

  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [errorOrders, setErrorOrders] = useState(null);

  const { store } = useContext(StoreContext);
  const [favoriteQuantity, setFavoriteQuantity] = useState(0);

  const router = useRouter();
  const initialized = useRef(false);

  const isAuthenticated = !!userData;

  // Mark as hydrated once on client
  useEffect(() => {
    setHydrated(true);
  }, []);

  // Initialize user only after hydration
  useEffect(() => {
    if (!hydrated || initialized.current) return;
    initialized.current = true;

    const initUser = async () => {
      try {
        const { configureAmplify } = await import("@/lib/amplify");
        configureAmplify();

        const { fetchAuthSession } = await import("aws-amplify/auth");
        const session = await fetchAuthSession();

        if (session?.tokens?.accessToken) {
          const res = await fetch("/api/jane-user", {
            headers: { Authorization: `Bearer ${session.tokens.accessToken}` },
          });
          const data = await res.json();
          setUserData(data?.data);
        }
      } catch (err) {
        console.error("User init error:", err);
        setUserData(null);
      } finally {
        setLoadingUser(false);
      }
    };

    initUser();
  }, [hydrated]);

  const fetchUserData = async () => {
    const { fetchAuthSession } = await import("aws-amplify/auth");
    const session = await fetchAuthSession();
    const token = session?.tokens?.accessToken;
    if (!token) throw new Error("No token");

    const res = await fetch("/api/jane-user", {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!res.ok) throw new Error("User fetch failed");
    const json = await res.json();
    return json.data;
  };

  const handleSignIn = async (username, password) => {
    setLoadingUser(true);
    try {
      const { signIn } = await import("aws-amplify/auth");
      const { isSignedIn } = await signIn({ username, password });

      if (isSignedIn) {
        const user = await fetchUserData();
        setUserData(user);
        await syncFavorites(user);
        return { success: true };
      }

      return { success: false, message: "Further steps required" };
    } catch (err) {
      console.error("Sign-in error:", err);
      return { success: false, message: err.message || "Failed to sign in" };
    } finally {
      setLoadingUser(false);
    }
  };

  const handleSignOut = async () => {
    try {
      const { signOut } = await import("aws-amplify/auth");
      await signOut();
    } finally {
      setUserData(null);
      router.replace("/login");
    }
  };

  const handleUpdatePassword = async (oldPassword, newPassword) => {
    try {
      const { updatePassword } = await import("aws-amplify/auth");
      await updatePassword({ oldPassword, newPassword });
      return { success: true, message: "Password updated." };
    } catch (err) {
      return { success: false, message: err.message || "Failed to update password." };
    }
  };

  const fetchAllOrders = useCallback(async () => {
    setLoadingOrders(true);
    try {
      const { fetchAuthSession } = await import("aws-amplify/auth");
      const session = await fetchAuthSession();
      const token = session?.tokens?.accessToken;
      if (!token) throw new Error("No token");

      const res = await fetch("/api/jane-orders", {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) throw new Error("Order fetch failed");
      const data = await res.json();
      setOrders(data.orders || []);
    } catch (err) {
      setErrorOrders(err.message);
    } finally {
      setLoadingOrders(false);
    }
  }, []);

  const syncFavorites = async (userData) => {
    try {
      const { fetchAuthSession } = await import("aws-amplify/auth");
      const session = await fetchAuthSession();
      const token = session?.tokens?.accessToken;
      if (!token) throw new Error("Unauthorized");

      const local = JSON.parse(localStorage.getItem("favorites") || "[]");
      const backend = userData?.product_content?.product_favorites || [];

      const backendSet = new Set(
        backend.map((f) => `${f.product_id}-${f.store_id}`)
      );

      const missing = local.filter(
        (f) => !backendSet.has(`${f.productId}-${f.storeId}`)
      );

      for (const fav of missing) {
        await fetch("/api/jane-favorites", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            product_id: fav.productId,
            store_id: fav.storeId,
          }),
        });
      }

      if (missing.length) {
        const updated = await fetchUserData();
        setUserData(updated);
        localStorage.removeItem("favorites");
      }
    } catch (error) {
      console.error("Favorite sync error:", error);
    }
  };

  const addFavorite = async (productId, storeId) => {
    try {
      if (!isAuthenticated) {
        const favs = JSON.parse(localStorage.getItem("favorites") || "[]");
        if (!favs.find(f => f.productId === productId)) {
          favs.push({ productId, storeId });
          localStorage.setItem("favorites", JSON.stringify(favs));
        }
        return;
      }

      const { fetchAuthSession } = await import("aws-amplify/auth");
      const session = await fetchAuthSession();
      const token = session?.tokens?.accessToken;
      if (!token) throw new Error("Unauthorized");

      await fetch("/api/jane-favorites", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ product_id: productId, store_id: storeId }),
      });

      const updated = await fetchUserData();
      setUserData(updated);
    } catch (err) {
      console.error("Add favorite error:", err);
    }
  };

  const removeFavorite = async (productId) => {
    try {
      if (!isAuthenticated) {
        let favs = JSON.parse(localStorage.getItem("favorites") || "[]");
        favs = favs.filter(f => f.productId !== productId);
        localStorage.setItem("favorites", JSON.stringify(favs));
        return;
      }

      const { fetchAuthSession } = await import("aws-amplify/auth");
      const session = await fetchAuthSession();
      const token = session?.tokens?.accessToken;
      if (!token) throw new Error("Unauthorized");

      await fetch(`/api/jane-favorites?favorite_id=${productId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      const updated = await fetchUserData();
      setUserData(updated);
    } catch (err) {
      console.error("Remove favorite error:", err);
    }
  };

  const updateUserData = async (updatedData) => {
    try {
      const { fetchAuthSession } = await import("aws-amplify/auth");
      const session = await fetchAuthSession();
      const token = session?.tokens?.accessToken;
      if (!token) throw new Error("No token");

      const res = await fetch("/api/jane-user", {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(updatedData),
      });

      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setUserData(data?.data);
    } catch (err) {
      setErrorUser(err.message);
    }
  };

  return (
    <UserContext.Provider
      value={{
        isAuthenticated,
        userData,
        loadingUser,
        errorUser,
        orders,
        loadingOrders,
        errorOrders,
        fetchAllOrders,
        favoriteQuantity,
        addFavorite,
        removeFavorite,
        updatePassword: handleUpdatePassword,
        updateUserData,
        signIn: handleSignIn,
        signOut: handleSignOut,
      }}
    >
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  return useContext(UserContext);
}
